import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";

const JOURNEY = [
  { n: "01", t: "Apply", d: "Tell us about your domain expertise and complete your profile." },
  { n: "02", t: "Train", d: "Prepare for the specific demands of AI evaluation." },
  { n: "03", t: "Assess", d: "Demonstrate your ability through practical, domain-specific tasks." },
  { n: "04", t: "Become FIAD Vetted", d: "Experts who meet the standard enter the relevant expert pool." },
  { n: "05", t: "Be considered for relevant opportunities", d: "When suitable work is available, vetted experts are considered." },
  { n: "06", t: "Contribute to approved work", d: "Eligible contributors are paid competitively for approved project work." },
];

const PROFILES = ["Native Bengali speakers", "Bilingual speakers", "Language professionals", "Translators", "Writers", "Editors", "Teachers", "Researchers", "Academics", "Professionals with substantial Bengali-language experience"];

export default function Experts() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F6F3EC]">
      <SiteNav />

      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-20 sm:pt-32 sm:pb-24">
          <p className="eyebrow text-[#C79447] reveal">For experts</p>
          <h1 className="mt-6 font-display text-4xl sm:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl reveal reveal-1">Your expertise can shape better AI.</h1>
          <p className="mt-8 text-lg sm:text-xl text-[#D4D0C6] leading-relaxed max-w-3xl reveal reveal-2">
            FIAD is building a network of specialised experts for AI evaluation across languages, law, healthcare, coding and other domains. Expertise is the starting point — FIAD adds training, assessment, vetting and project readiness.
          </p>
        </div>
      </section>

      {/* NOW ONBOARDING */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28" data-testid="experts-now-onboarding">
          <p className="eyebrow text-[#C79447]">Now onboarding</p>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">Bengali Linguistic Experts</h2>
          <div className="mt-8 h-px w-full bg-white/10" />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
            <p className="lg:col-span-7 text-lg text-[#D4D0C6] leading-relaxed">We're currently reviewing applications from people with genuine Bengali-language expertise — native speakers, bilingual speakers, and professionals with substantial relevant experience.</p>
            <div className="lg:col-span-5 flex flex-col gap-4">
              <button data-testid="experts-apply-btn" onClick={() => navigate("/apply")} className="inline-flex items-center justify-between gap-2 bg-[#F6F3EC] text-[#0A0A0A] px-7 py-4 text-sm font-medium hover:bg-white transition-colors">Apply for the Bengali Linguist Cohort <ArrowRight className="h-4 w-4" /></button>
              <p className="text-sm text-[#71717A] leading-relaxed">Other domain? Your field isn't open yet, but that doesn't mean we won't need it.</p>
              <button data-testid="experts-waitlist-btn" onClick={() => navigate("/waitlist")} className="inline-flex items-center gap-2 text-sm text-[#F6F3EC] link-underline w-fit">Join the Waitlist <ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </section>

      {/* WHO SHOULD APPLY */}
      <section className="border-b border-white/10 bg-[#0C0C0C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow text-[#71717A]">Who should apply</p>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12]">Relevant profiles.</h2>
            <p className="mt-5 text-sm text-[#71717A] leading-relaxed">Applying does not mean automatic acceptance. Every application is reviewed against the requirements of the work.</p>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
              {PROFILES.map((p) => (
                <div key={p} className="border-b border-white/10 py-4 text-[#D4D0C6]">{p}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EXPERT JOURNEY */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <p className="eyebrow text-[#71717A]">The expert journey</p>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight mb-12">From application to approved work.</h2>
          <div className="border-t border-white/10">
            {JOURNEY.map((s) => (
              <div key={s.n} className="grid grid-cols-12 gap-4 sm:gap-8 items-baseline border-b border-white/10 py-8 group">
                <span className="col-span-12 sm:col-span-2 font-mono-num text-4xl sm:text-6xl font-light text-white/15 group-hover:text-[#C79447] transition-colors">{s.n}</span>
                <h3 className="col-span-12 sm:col-span-4 font-display text-xl sm:text-2xl font-medium tracking-tight">{s.t}</h3>
                <p className="col-span-12 sm:col-span-6 text-base text-[#A1A1AA] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPENSATION */}
      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#C79447]">Compensation</p>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12]">Expertise should be valued.</h2>
          </div>
          <div className="lg:col-span-7 lg:pt-12 space-y-5">
            <p className="text-lg text-[#D4D0C6] leading-relaxed">Eligible contributors are paid competitively for approved project work.</p>
            <p className="text-sm text-[#71717A] leading-relaxed">FIAD does not guarantee acceptance, project allocation, employment or income. FIAD never charges experts.</p>
            <button data-testid="experts-apply-btn-2" onClick={() => navigate("/apply")} className="inline-flex items-center gap-2 bg-[#F6F3EC] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:bg-white transition-colors">Apply now <ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
